import { useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { useAuthStore, useAuthActions, useAuthState } from '../../stores/authStore';
import { loginRequest } from '../../config/authConfig';

// Configuración para Microsoft Graph API
const GRAPH_CONFIG = {
  GRAPH_ME_ENDPOINT: 'https://graph.microsoft.com/v1.0/me',
  GRAPH_PHOTO_ENDPOINT: 'https://graph.microsoft.com/v1.0/me/photo/$value',
};

export const useAuth = () => {
  const { instance, accounts, inProgress } = useMsal();
  const authState = useAuthState();

  // Use individual selectors to prevent unnecessary re-renders
  const account = useAuthStore((state) => state.account);
  const userProfile = useAuthStore((state) => state.userProfile);
  const userPhoto = useAuthStore((state) => state.userPhoto);
  const accessToken = useAuthStore((state) => state.accessToken);
  const error = useAuthStore((state) => state.error);
  const loginError = useAuthStore((state) => state.loginError);

  // Get stable references to actions
  const setUserProfile = useAuthStore((state) => state.setUserProfile);
  const setUserPhoto = useAuthStore((state) => state.setUserPhoto);
  const loginStart = useAuthStore((state) => state.loginStart);
  const loginSuccess = useAuthStore((state) => state.loginSuccess);
  const loginFailure = useAuthStore((state) => state.loginFailure);
  const logoutStart = useAuthStore((state) => state.logoutStart);
  const logoutComplete = useAuthStore((state) => state.logoutComplete);
  const resetAuth = useAuthStore((state) => state.resetAuth);
  const setInitialized = useAuthStore((state) => state.setInitialized);
  const setTokens = useAuthStore((state) => state.setTokens);
  const clearErrors = useAuthStore((state) => state.clearErrors);

  // Obtener datos del usuario desde Microsoft Graph
  const fetchUserProfile = useCallback(async (accessToken: string) => {
    try {
      const response = await fetch(GRAPH_CONFIG.GRAPH_ME_ENDPOINT, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const profile = await response.json();
        setUserProfile({
          id: profile.id,
          name: profile.displayName,
          email: profile.mail || profile.userPrincipalName,
          givenName: profile.givenName,
          surname: profile.surname,
          jobTitle: profile.jobTitle,
          department: profile.department,
          officeLocation: profile.officeLocation,
          mobilePhone: profile.mobilePhone,
          businessPhones: profile.businessPhones,
          userPrincipalName: profile.userPrincipalName,
        });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  }, [setUserProfile]);

  // Obtener foto del usuario
  const fetchUserPhoto = useCallback(async (accessToken: string) => {
    try {
      const response = await fetch(GRAPH_CONFIG.GRAPH_PHOTO_ENDPOINT, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const photoUrl = URL.createObjectURL(blob);
        setUserPhoto(photoUrl);
      }
    } catch (error) {
      console.error('Error fetching user photo:', error);
    }
  }, [setUserPhoto]);

  // Obtener token de acceso
  const getAccessToken = useCallback(async (account: AccountInfo) => {
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      return response.accessToken;
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  }, [instance]);

  const loginSuccessAuth = useCallback(async(response: any) => {
    loginSuccess(response.account, {
      accessToken: response.accessToken,
      idToken: response.idToken || '',
    });
  }, [loginSuccess])

  // Helper function for popup login
  const handlePopupLogin = useCallback(async () => {
    const response = await instance.loginPopup(loginRequest);
    
    if (response?.account) {
      loginSuccessAuth(response);
      
      if (response.accessToken) {
        await Promise.all([
          fetchUserProfile(response.accessToken),
          fetchUserPhoto(response.accessToken),
        ]);
      }
    }
  }, [instance, loginSuccessAuth, fetchUserProfile, fetchUserPhoto]);

  // Función de login
  const login = useCallback(async (usePopup: boolean = true) => {
    loginStart();

    try {
      if (usePopup) {
        await handlePopupLogin();
      } else {
        await instance.loginRedirect(loginRequest);
      }
    } catch (error: any) {
      console.error('Login failed:', error);
      loginFailure(error.message || 'Login failed');
    }
  }, [instance, loginStart, loginFailure, handlePopupLogin]);

  // Función de logout
  const logout = useCallback(async (usePopup: boolean = true) => {
    logoutStart();

    try {
      if (usePopup) {
        await instance.logoutPopup({
          postLogoutRedirectUri: '/',
          mainWindowRedirectUri: '/',
        });
      } else {
        await instance.logoutRedirect({
          postLogoutRedirectUri: '/',
        });
      }

      logoutComplete();
    } catch (error: any) {
      console.error('Logout failed:', error);
      logoutComplete(); // Completar logout aunque falle
    }
  }, [instance, logoutStart, logoutComplete]);


  const processRedirectResult = useCallback(async (redirectResult: any) => {
    loginSuccess(redirectResult.account, {
      accessToken: redirectResult.accessToken,
      idToken: redirectResult.idToken,
    });

    if (redirectResult.accessToken) {
      await Promise.all([
        fetchUserProfile(redirectResult.accessToken),
        fetchUserPhoto(redirectResult.accessToken),
      ]);
    }
  }, [loginSuccess, fetchUserProfile, fetchUserPhoto]);

  const handleTokenAcquisition = useCallback(async (currentAccount: AccountInfo) => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: currentAccount,
      });

      if (tokenResponse?.accessToken) {
        loginSuccessAuth(currentAccount);
        await Promise.all([
          fetchUserProfile(tokenResponse.accessToken),
          fetchUserPhoto(tokenResponse.accessToken),
        ]);
        return true;
      }
      return false;
    } catch (tokenError) {
      return false;
    }
  }, [instance, loginSuccessAuth, fetchUserProfile, fetchUserPhoto]);

  const handleAccountWithoutToken = useCallback((currentAccount: AccountInfo) => {
    if (!currentAccount.username) {
      resetAuth();
      return;
    }

    loginSuccessAuth(null);
    const basicProfile = {
      id: currentAccount.localAccountId,
      name: currentAccount.name || currentAccount.username,
      email: currentAccount.username,
      userPrincipalName: currentAccount.username,
    };
    setUserProfile(basicProfile);
  }, [loginSuccessAuth, resetAuth, setUserProfile]);

  const processExistingAccount = useCallback(async () => {
    const currentAccount = instance.getActiveAccount() || accounts[0];
    
    if (!currentAccount) {
      if (!authState.isAuthenticated) {
        resetAuth();
      }
      return;
    }

    const tokenAcquired = await handleTokenAcquisition(currentAccount);
    if (!tokenAcquired) {
      handleAccountWithoutToken(currentAccount);
    }
  }, [instance, accounts, authState.isAuthenticated, resetAuth, handleTokenAcquisition, handleAccountWithoutToken]);

  // Inicializar el estado de autenticación
  const handleAuthInitialization = useCallback(async () => {
    if (inProgress !== InteractionStatus.None || authState.isInitialized) {
      return;
    }

    try {
      const redirectResult = await instance.handleRedirectPromise();
      
      if (redirectResult?.account) {
        await processRedirectResult(redirectResult);
      } else {
        await processExistingAccount();
      }
    } catch (error) {
      console.error('Error during auth initialization:', error);
      resetAuth();
    } finally {
      setInitialized(true);
    }
  }, [
    instance,
    inProgress,
    authState.isInitialized,
    processRedirectResult,
    processExistingAccount,
    resetAuth,
    setInitialized
  ]);

  useEffect(() => {
    handleAuthInitialization();
  }, [
    handleAuthInitialization,
    accounts,
    authState.isAuthenticated,
    loginSuccess,
    fetchUserProfile,
    fetchUserPhoto,
  ]);

  // Renovar token automáticamente
  const refreshToken = useCallback(async () => {
    const currentAccount = instance.getActiveAccount();
    if (currentAccount) {
      const accessToken = await getAccessToken(currentAccount);
      if (accessToken) {
        setTokens(accessToken, '');
        return accessToken;
      }
    }
    return null;
  }, [instance, getAccessToken, setTokens]);

  return {
    // Estado
    ...authState,

    // Acciones
    login,
    logout,
    refreshToken,

    // Datos del usuario (usando selectores optimizados)
    account,
    userProfile,
    userPhoto,

    // Tokens
    accessToken,

    // Errores
    error,
    loginError,
    clearErrors,
  };
};
